import { useMemo, useState } from 'react';

import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import { useCreateFormStore } from '@components/form';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconHelpSimple } from '@components/icons/help-simple';
import { IconList } from '@components/icons/list';
import { IconMarketplace } from '@components/icons/marketplace';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconReplace } from '@components/icons/replace';
import { IconSearch } from '@components/icons/search';
import { List } from '@components/integrations/integrations-types';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { SearchBox } from '@components/search-box';
import { Sidebar, forceHideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';

import { TravellerAttributeTemplateData } from 'api/attributes/requests';
import { useLists } from 'api/lists/hooks';

import {
  DetailsSection,
  FieldFormSection,
  FieldFormWithUnsavedChangesPrompt,
  FieldFormData,
  Form,
  FormFooter,
  applyInputStatus,
  locale,
} from './field-form-shared';

// =============================================================================
// Multi-step flow controller
// =============================================================================

type SelectFieldStep = 'listSource' | 'listSelection' | 'form';

type SelectFieldFlowProps = {
  attributeType: 'single_select' | 'multi_select';
  attribute?: TravellerAttributeTemplateData;
  isTravellerAttribute: boolean;
  onSubmit: (data: FieldFormData) => Promise<boolean>;
  isEditing: boolean;
};

export const SelectFieldFlow = ({
  attributeType,
  attribute,
  isTravellerAttribute,
  onSubmit,
  isEditing,
}: SelectFieldFlowProps) => {
  // Include hidden lists so we can display them when editing fields created in form builder
  const listsQuery = useLists('?include_hidden=1');

  // Determine initial step based on mode
  const initialStep: SelectFieldStep = isEditing ? 'form' : 'listSource';
  const [step, setStep] = useState<SelectFieldStep>(initialStep);
  const [selectedListId, setSelectedListId] = useState<string | undefined>(
    attribute?.options ?? undefined
  );

  const selectedList = useMemo(() => {
    if (!selectedListId || !listsQuery.data) {
      return undefined;
    }
    return listsQuery.data.find((list) => {
      return list.shortId === selectedListId;
    });
  }, [selectedListId, listsQuery.data]);

  const handleSelectList = (listId: string) => {
    setSelectedListId(listId);
    setStep('form');
  };

  const handleChangeList = () => {
    setStep('listSelection');
  };

  if (step === 'listSource') {
    return (
      <ListSourceSelection
        onSelectGlobalLists={() => {
          return setStep('listSelection');
        }}
      />
    );
  }

  if (step === 'listSelection') {
    return (
      <ListSelection
        lists={listsQuery.data ?? []}
        isLoading={listsQuery.isLoading}
        onSelectList={handleSelectList}
      />
    );
  }

  // Still loading lists
  if (listsQuery.isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <SelectFieldForm
      attributeType={attributeType}
      attribute={attribute}
      selectedList={selectedList}
      isTravellerAttribute={isTravellerAttribute}
      onSubmit={onSubmit}
      isEditing={isEditing}
      onChangeList={handleChangeList}
    />
  );
};

// =============================================================================
// Step 1 — Pick list source
// =============================================================================

type ListSourceSelectionProps = {
  onSelectGlobalLists: () => void;
};

const ListSourceSelection = ({ onSelectGlobalLists }: ListSourceSelectionProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Slide key="list-source-selection" offset={30}>
      <Stack space={6}>
        <FieldFormSection label={t('listType')}>
          <Stack space={2}>
            <Card space={3} variant="transparent" interactive onClick={onSelectGlobalLists}>
              <Stack orientation="horizontal" align="center" space={3}>
                <IconFancyContainer icon={<IconCheckmark />} variant="blue" size="md" />
                <Text text={t('selectFromGlobalLists')} font="semibold12" />
              </Stack>
            </Card>
            <Card space={3} variant="default" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
              <Stack orientation="horizontal" align="center" space={3}>
                <IconFancyContainer icon={<IconMarketplace />} variant="gray" size="md" />
                <Text text={t('selectFromMarketplace')} font="semibold12" color="textSecondary" />
              </Stack>
            </Card>
          </Stack>
        </FieldFormSection>
      </Stack>
      <Sidebar.Footer>
        <Form.Actions>
          <Button text={t('core.cancel')} variant="secondary" onClick={forceHideSidebar} />
          <Button text={t('createField')} variant="primary-blue" isDisabled />
        </Form.Actions>
      </Sidebar.Footer>
    </Slide>
  );
};

// =============================================================================
// Step 2 — Pick a list
// =============================================================================

type ListSelectionProps = {
  lists: List[];
  isLoading: boolean;
  onSelectList: (listId: string) => void;
};

const ListSelection = ({ lists, isLoading, onSelectList }: ListSelectionProps) => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');

  // Filter out hidden lists (those are managed by form builder, not selectable here)
  const selectableLists = useMemo(() => {
    return lists.filter((list) => {
      return !list.isHidden;
    });
  }, [lists]);

  // Apply search filter
  const filteredLists = useMemo(() => {
    if (!searchStr) {
      return selectableLists;
    }
    const search = searchStr.toLowerCase();
    return selectableLists.filter((list) => {
      return list.name.toLowerCase().includes(search);
    });
  }, [selectableLists, searchStr]);

  const hasNoLists = selectableLists.length === 0;
  const hasNoResults = searchStr && filteredLists.length === 0;

  if (isLoading) {
    return <RelativeSpinner />;
  }

  const renderContent = () => {
    if (hasNoLists) {
      return (
        <EmptyState
          variant="small"
          icon={<IconList />}
          header={t('noLists')}
          content={t('noListsDescription')}
          skipMargin
        />
      );
    }

    return (
      <Stack space={4}>
        <SearchBox placeholder={t('searchForList')} value={searchStr} onChange={setSearchStr} />
        {hasNoResults ? (
          <EmptyState
            variant="small"
            icon={<IconSearch />}
            header={t('noResultsFound')}
            content={t('noResultsFoundDescription')}
            skipMargin
          />
        ) : (
          <Stack space={2}>
            {filteredLists.map((list) => {
              return (
                <Card key={list.shortId} spaceX={4} spaceY={3} variant="default">
                  <Stack orientation="horizontal" align="center" justify="space-between" space={2}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text text={list.name} font="semibold14" ellipsis />
                    </div>
                    <Button
                      text={t('select')}
                      variant="primary-blue"
                      size="sm"
                      onClick={() => {
                        return onSelectList(list.shortId);
                      }}
                    />
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Slide key="list-selection" offset={30}>
      <Stack space={6}>
        <FieldFormSection label={t('selectList')}>{renderContent()}</FieldFormSection>
      </Stack>
    </Slide>
  );
};

// =============================================================================
// Step 3 — Configure the field
// =============================================================================

type SelectFieldFormProps = {
  attributeType: 'single_select' | 'multi_select';
  attribute?: TravellerAttributeTemplateData;
  selectedList?: List;
  isTravellerAttribute: boolean;
  onSubmit: (data: FieldFormData) => Promise<boolean>;
  isEditing: boolean;
  onChangeList: () => void;
};

const SelectFieldForm = ({
  attributeType,
  attribute,
  selectedList,
  isTravellerAttribute,
  onSubmit,
  isEditing,
  onChangeList,
}: SelectFieldFormProps) => {
  const formStore = useCreateFormStore();
  const isFormDirty = formStore.useIsDirty();
  const isListMissing = !selectedList;
  const isListChanged = !isListMissing && selectedList.shortId !== attribute?.options;
  const isDirty = isFormDirty || isListChanged;

  const handleSubmit = async (data: FieldFormData) => {
    const submitData = applyInputStatus(
      { ...data, attributeType, options: selectedList?.shortId ?? '' },
      isTravellerAttribute
    );
    return onSubmit(submitData);
  };

  return (
    <Slide key="select-field-form" offset={30}>
      <FieldFormWithUnsavedChangesPrompt
        onSubmit={handleSubmit}
        store={formStore}
        overwriteIsDirty={isDirty}
      >
        <Stack space={6}>
          <DetailsSection
            attributeType={attributeType}
            attribute={attribute}
            isTravellerAttribute={isTravellerAttribute}
          >
            <SelectedListCard list={selectedList} onChangeList={onChangeList} />
          </DetailsSection>
        </Stack>
        <FormFooter isEditing={isEditing} isDirty={isDirty} isDisabled={isListMissing} />
      </FieldFormWithUnsavedChangesPrompt>
    </Slide>
  );
};

// =============================================================================
// Card showing the currently-selected list inside the configuration step
// =============================================================================

type SelectedListCardProps = {
  list?: List;
  onChangeList?: () => void;
};

const SelectedListCard = ({ list, onChangeList }: SelectedListCardProps) => {
  const { t } = useScopedTranslate(locale);

  const hasList = Boolean(list);
  const isHidden = list?.isHidden ?? false;
  const displayName = !list
    ? t('listNotFound')
    : isHidden
    ? t('listInsideExternalForm')
    : list.name;
  const textColor: Color | undefined = hasList ? undefined : 'red100';
  const showChangeOption = onChangeList && !isHidden;

  return (
    <Card space={3} variant="default">
      <Stack orientation="horizontal" align="center" justify="space-between" space={2}>
        <Stack orientation="horizontal" align="center" space={4} style={{ flex: 1, minWidth: 0 }}>
          {hasList ? <IconList color="elementPurple80" /> : <IconHelpSimple color="red100" />}
          <Text text={displayName} font="semibold14" color={textColor} ellipsis={hasList} />
        </Stack>
        {showChangeOption && (
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
            }}
          >
            <Menu.Item label={t('replaceList')} icon={<IconReplace />} onSelect={onChangeList} />
          </Menu>
        )}
      </Stack>
    </Card>
  );
};
